import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  disableTotp,
  getMfaStatus,
  getRecoveryCodes,
  regenerateRecoveryCodes,
  type MfaStatusData,
  type RecoveryCodesData,
} from '../lib/api';
import AuthLayout from './AuthLayout';

export default function MfaSettingsPage() {
  const [searchParams] = useSearchParams();
  const projectId = useMemo(() => searchParams.get('project_id') ?? '', [searchParams]);
  const accessToken = useMemo(() => searchParams.get('access_token') ?? '', [searchParams]);

  const [status, setStatus] = useState<MfaStatusData | null>(null);
  const [codes, setCodes] = useState<RecoveryCodesData | null>(null);
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'regenerate' | 'disable' | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !accessToken) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getMfaStatus(projectId, accessToken),
      getRecoveryCodes(projectId, accessToken),
    ]).then(([statusResult, codesResult]) => {
      if (cancelled) return;
      if (statusResult.success && statusResult.data) {
        setStatus(statusResult.data);
      } else {
        setError(statusResult.error ?? 'Failed to load MFA status.');
      }
      if (codesResult.success && codesResult.data) {
        setCodes(codesResult.data);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, accessToken]);

  if (!projectId || !accessToken) {
    return (
      <AuthLayout
        title="Sign in required"
        subtitle="Open MFA settings from your account; this page needs an access token."
      >
        <p className="auth-help">
          <Link to="/login">Back to sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  async function handleRegenerate() {
    setBusy('regenerate');
    setError(null);
    const result = await regenerateRecoveryCodes(projectId, accessToken);
    setBusy(null);
    if (result.success && result.data) {
      setNewCodes(result.data.recovery_codes);
      setCodes(prev =>
        prev
          ? { ...prev, remaining_codes: result.data!.recovery_codes.length, used_codes: 0 }
          : prev
      );
    } else {
      setError(result.error ?? 'Could not regenerate recovery codes.');
    }
  }

  async function handleDisable() {
    setBusy('disable');
    setError(null);
    const result = await disableTotp(projectId, accessToken);
    setBusy(null);
    if (result.success) {
      setStatus(prev =>
        prev
          ? {
              ...prev,
              mfa_enabled: false,
              methods_enabled: [],
              totp: { ...prev.totp, enabled: false },
            }
          : prev
      );
      setConfirmDisable(false);
    } else {
      setError(result.error ?? 'Could not disable MFA.');
    }
  }

  if (loading) {
    return <AuthLayout title="Two-factor authentication" subtitle="Loading…" />;
  }

  if (!status) {
    return <AuthLayout title="Two-factor authentication" subtitle={error ?? 'Failed to load.'} />;
  }

  return (
    <AuthLayout
      title="Two-factor authentication"
      subtitle={
        status.mfa_enabled
          ? 'MFA is currently active on your account.'
          : 'Add a second step to protect your sign-in.'
      }
    >
      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}

      {!status.mfa_enabled ? (
        <div className="auth-section">
          <p className="auth-help">
            Project policy: <strong>{status.project_mfa_policy}</strong>.
            {status.mfa_required ? ' MFA is required for this project.' : ''}
          </p>
          <Link
            to={`/mfa/setup?project_id=${projectId}&access_token=${accessToken}`}
            className="auth-submit"
          >
            Set up MFA
          </Link>
        </div>
      ) : (
        <>
          <div className="auth-section">
            <h2 className="auth-section-title">Authenticator app</h2>
            <p className="auth-help">
              {status.totp.enabled ? 'TOTP enabled' : 'TOTP not enabled'}
              {status.totp.last_used_at
                ? ` — last used ${new Date(status.totp.last_used_at).toLocaleString()}`
                : ''}
            </p>
          </div>

          <div className="auth-section">
            <h2 className="auth-section-title">Recovery codes</h2>
            {codes ? (
              <p className="auth-help">
                {codes.remaining_codes} of {codes.total_codes} codes remaining.
              </p>
            ) : null}
            <button
              type="button"
              className="auth-secondary"
              onClick={handleRegenerate}
              disabled={busy === 'regenerate'}
            >
              {busy === 'regenerate' ? 'Generating…' : 'Regenerate recovery codes'}
            </button>
            {newCodes ? (
              <>
                <p className="auth-help">
                  <strong>Save these codes — your old codes are now invalid.</strong>
                </p>
                <ul className="auth-codes">
                  {newCodes.map(rc => (
                    <li key={rc}>
                      <code>{rc}</code>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>

          <div className="auth-section auth-danger">
            <h2 className="auth-section-title">Disable MFA</h2>
            {status.mfa_required ? (
              <p className="auth-help">MFA is required by this project and can’t be disabled.</p>
            ) : !confirmDisable ? (
              <button
                type="button"
                className="auth-danger-button"
                onClick={() => setConfirmDisable(true)}
              >
                Disable MFA
              </button>
            ) : (
              <>
                <p className="auth-help">
                  This will turn off MFA and invalidate your recovery codes. Are you sure?
                </p>
                <button
                  type="button"
                  className="auth-danger-button"
                  onClick={handleDisable}
                  disabled={busy === 'disable'}
                >
                  {busy === 'disable' ? 'Disabling…' : 'Yes, disable MFA'}
                </button>
                <button
                  type="button"
                  className="auth-secondary"
                  onClick={() => setConfirmDisable(false)}
                  disabled={busy === 'disable'}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </>
      )}
    </AuthLayout>
  );
}
